import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import logoWhite from '../../assets/images/logo-w.svg';
import * as API from '../../../api';

/**
 * New Tafwija footer — replaces the legacy MUI/SCSS Footer at
 * shared/components/Layout/Footer. Built with Tailwind to match the
 * rest of the redesigned home page.
 *
 * Preserves the legacy data integrations:
 *   - About us text + socials + phone come from /api/settings
 *   - WhatsApp floating button uses settings.phone
 *
 * Mounted globally from App.js so every page gets the same footer.
 */

function InlineFacebook(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.128 22 16.991 22 12Z" />
    </svg>
  );
}

function InlineInstagram(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden {...props}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function InlineWhatsapp(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M20.52 3.48A11.97 11.97 0 0 0 12.06 0C5.5.02.27 5.27.27 11.83c0 2.08.55 4.11 1.59 5.9L0 24l6.42-1.68a11.8 11.8 0 0 0 5.64 1.43h.01c6.55 0 11.79-5.25 11.79-11.8 0-3.15-1.23-6.11-3.34-8.47ZM12.07 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.81 1 1.02-3.71-.24-.38a9.83 9.83 0 0 1-1.5-5.29c0-5.43 4.42-9.86 9.85-9.86 2.63 0 5.1 1.03 6.96 2.9a9.78 9.78 0 0 1 2.88 6.97c-.01 5.43-4.43 9.86-9.86 9.86Zm5.4-7.39c-.3-.15-1.75-.86-2.03-.96-.27-.1-.47-.15-.66.15-.2.3-.76.96-.93 1.16-.17.2-.34.22-.64.07-.3-.15-1.25-.46-2.39-1.47-.88-.78-1.48-1.74-1.65-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.34.45-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.51-.07-.15-.66-1.6-.91-2.19-.24-.58-.49-.5-.66-.51l-.56-.01c-.2 0-.51.07-.78.37-.27.3-1.03 1-1.03 2.44 0 1.44 1.05 2.83 1.2 3.03.15.2 2.07 3.16 5.02 4.43.7.3 1.25.48 1.68.62.7.22 1.34.19 1.85.12.56-.08 1.75-.71 2-1.4.25-.69.25-1.28.17-1.4-.07-.13-.27-.2-.57-.35Z" />
    </svg>
  );
}

export default function Footer() {
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState({});
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    let alive = true;
    API.Settings()
      .then((res) => alive && setSettings(res?.settings || {}))
      .catch(() => alive && setSettings({}));
    return () => { alive = false; };
  }, []);

  const switchLang = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    // Real newsletter API not wired yet — show success state for now.
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
    }
  };

  const exploreLinks = [
    { to: '/events?type=Activitie', label: t('footer.exploreActivities') },
    { to: '/events?type=Event',     label: t('footer.exploreTrips') },
    { to: '/events?type=Sejour',    label: t('footer.exploreStays') },
    { to: '/galleries',             label: t('footer.exploreGalleries') },
    { to: '/blog',                  label: t('footer.exploreBlog') },
  ];
  const companyLinks = [
    { to: '/contact',         label: t('footer.companyContact') },
    { to: '/faqs',            label: t('footer.companyFaq') },
    { to: '/cgu',             label: t('footer.companyCgu') },
    { to: '/become-partner',  label: t('footer.companyPartner') },
  ];
  const year = new Date().getFullYear();
  const phone = (settings.phone || '').toString().replace(/\D/g, '');

  return (
    <>
      <footer
        className="relative isolate overflow-hidden text-slate-200"
        style={{ backgroundColor: '#03387d' }}
      >

        <div className="relative mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
          {/* Top grid */}
          <div className="grid gap-8 py-10 sm:gap-12 sm:py-16 lg:grid-cols-12">
            {/* Brand column */}
            <div className="lg:col-span-4">
              <Link to="/" className="inline-flex items-center">
                <img src={logoWhite} alt="Tafwija" className="h-20 w-auto" />
              </Link>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-300">
                {t('footer.tagline')}
              </p>

              {/* Contact rows */}
              <ul className="mt-6 space-y-2.5 text-sm text-slate-300">
                {/* Address hidden for now — flip back to `settings.address` when ready to show it again. */}
                {false && settings.address ? (
                  <li className="flex items-start gap-2.5">
                    <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent-500" />
                    <span>{settings.address}</span>
                  </li>
                ) : null}
                {settings.email ? (
                  <li className="flex items-center gap-2.5">
                    <EnvelopeIcon className="h-4 w-4 shrink-0 text-accent-500" />
                    <a href={`mailto:${settings.email}`} className="hover:text-white">{settings.email}</a>
                  </li>
                ) : null}
                {settings.phone ? (
                  <li className="flex items-center gap-2.5">
                    <PhoneIcon className="h-4 w-4 shrink-0 text-accent-500" />
                    <a href={`tel:${settings.phone}`} className="hover:text-white" dir="ltr">{settings.phone}</a>
                  </li>
                ) : null}
              </ul>

              {/* Socials */}
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  {t('footer.followUs')}
                </p>
                <div className="mt-2.5 flex items-center gap-2.5">
                  {settings.facebook ? (
                    <a
                      href={settings.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-slate-200 ring-1 ring-white/10 transition-all duration-200 hover:bg-accent-500 hover:text-brand-900 hover:ring-accent-500"
                    >
                      <InlineFacebook className="h-4 w-4" />
                    </a>
                  ) : null}
                  {settings.twitter ? (
                    <a
                      href={settings.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-slate-200 ring-1 ring-white/10 transition-all duration-200 hover:bg-accent-500 hover:text-brand-900 hover:ring-accent-500"
                    >
                      <InlineInstagram className="h-4 w-4" />
                    </a>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Explore + Company — side by side even on mobile to save vertical space */}
            <div className="grid grid-cols-2 gap-6 sm:contents">
              <div className="lg:col-span-2">
                <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-white">
                  {t('footer.exploreTitle')}
                </h3>
                <ul className="mt-3 space-y-2 text-sm sm:mt-4 sm:space-y-2.5">
                  {exploreLinks.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className="inline-flex items-center text-slate-300 transition-colors hover:text-white"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-2">
                <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-white">
                  {t('footer.companyTitle')}
                </h3>
                <ul className="mt-3 space-y-2 text-sm sm:mt-4 sm:space-y-2.5">
                  {companyLinks.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className="inline-flex items-center text-slate-300 transition-colors hover:text-white"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Newsletter */}
            <div className="lg:col-span-4">
              <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-white">
                {t('footer.newsletterTitle')}
              </h3>
              <p className="mt-3 text-sm text-slate-300">
                {t('footer.newsletterBody')}
              </p>
              <form onSubmit={handleSubscribe} className="mt-4">
                <div className="flex items-stretch gap-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('footer.newsletterPlaceholder')}
                    className="min-w-0 flex-1 rounded-full bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-400 ring-1 ring-white/10 transition focus:outline-none focus:ring-accent-500"
                  />
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-1.5 rounded-full bg-accent-500 px-4 py-2.5 text-sm font-semibold text-brand-900 transition-all duration-200 hover:bg-accent-400"
                  >
                    <span className="hidden sm:inline">{t('footer.newsletterSubmit')}</span>
                    <ArrowRightIcon className="h-4 w-4 rtl:rotate-180" />
                  </button>
                </div>
                {subscribed && (
                  <p className="mt-2 text-xs font-medium text-accent-500">
                    {t('footer.newsletterSuccess')}
                  </p>
                )}
              </form>

              {/* Language switcher */}
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  {t('footer.languageLabel')}
                </p>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  {['fr', 'en', 'ar'].map((code) => {
                    const active = i18n.language?.startsWith(code);
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => switchLang(code)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold uppercase transition-all duration-200 ${
                          active
                            ? 'bg-accent-500 text-brand-900'
                            : 'bg-white/5 text-slate-300 ring-1 ring-white/10 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        {code}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Secure-payment strip — required by PayZone on the home page
              footer, alongside a link to the CGV. The artwork ships on a white
              chip on purpose: the transparent variant puts VISA's navy mark
              straight onto this blue footer, where it all but disappears. */}
          <div className="flex flex-col items-start gap-4 border-t border-white/10 py-5 sm:flex-row sm:items-center sm:justify-between sm:py-6">
            <div className="rounded-xl bg-white px-3 py-2 shadow-card">
              <img
                src="/payzone-secure-light.png"
                alt={t('footer.securePaymentAlt')}
                width="1162"
                height="120"
                loading="lazy"
                className="block h-auto w-full max-w-[420px]"
              />
            </div>
            <Link
              to="/cgu"
              className="text-sm font-medium text-slate-200 underline decoration-white/30 underline-offset-4 transition-colors hover:text-white hover:decoration-white"
            >
              {t('footer.cgvFull')}
            </Link>
          </div>

          {/* Bottom bar */}
          <div className="flex flex-col items-start gap-1.5 border-t border-white/10 py-5 text-start text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:py-6">
            <p>
              © {year} <span className="font-semibold text-slate-200">Tafwija</span>. {t('footer.rights')}
            </p>
            <p>{t('footer.credit')}</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp button (legacy convention) */}
      {phone ? (
        <a
          href={`https://api.whatsapp.com/send?phone=${phone}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          className="fixed bottom-[max(1.25rem,env(safe-area-inset-bottom))] right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-elevated transition-transform hover:scale-110 sm:bottom-5 sm:right-5 sm:h-14 sm:w-14 rtl:right-auto rtl:left-4 sm:rtl:left-5"
        >
          <InlineWhatsapp className="h-7 w-7" />
        </a>
      ) : null}
    </>
  );
}
